export const accountNumberMask = (value: string, positions = 4) => {
  const regexObj = new RegExp(`\\d(?=\\d{${positions}})`, 'g');

  return value.replace(regexObj, '*');
};

export const formatPhone = (value: string) => {
  if (!value) return value;
  const phone = value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  const phoneInput = !phone![2]
    ? phone![1]
    : `(${phone![1]}) ${phone![2]}${phone![3] ? `-${phone![3]}` : ''}`;

  return phoneInput;
};

export enum DateFormats {
  SHORT = 'short',
  LONG = 'long',
  ONLY_YEAR = 'onlyYear',
  US = 'en-US',
  MONTH_YEAR = 'monthYear',
  DAY_MONTH_DATE = 'day/month/date',
  DAY_MONTH_DATE_Year = 'day/month/date,year',
  MONTH_DAY_YEAR = 'month/day,year',
  M_YEAR = 'MYear',
  SHORT_MONTH_DAY_YEAR = 'short/month/day,year',
}

export const formatDate = (
  value: Date,
  type: DateFormats = DateFormats.SHORT
) => {
  switch (type) {
    case 'short': {
      return new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
    case 'en-US': {
      return new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      });
    }
    case 'long': {
      return new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZoneName: 'short',
        timeZone: 'America/New_York',
      });
    }
    case 'onlyYear': {
      return new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
      });
    }
    case 'monthYear': {
      const calculatedDate = new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
      });

      return calculatedDate.split(' ').join(', ');
    }
    case 'MYear': {
      const calculatedDate = new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
      });

      return calculatedDate.split(' ').join(', ');
    }
    case 'day/month/date': {
      const month = new Date(value).toLocaleDateString('en-US', {
        month: 'long',
      });
      const todaysDay = value.getDay();
      const todaysDate = value.getDate();
      const weekday = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ];
      let day = weekday[todaysDay];

      return `${day}, ${month} ${todaysDate}`;
    }
    case 'day/month/date,year': {
      const month = new Date(value).toLocaleDateString('en-US', {
        month: 'long',
      });
      const todaysDay = value.getDay();
      const todaysDate = value.getDate();
      const year = value.getFullYear();
      const weekday = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ];
      let day = weekday[todaysDay];

      return `${day}, ${month} ${todaysDate}, ${year} `;
    }
    case 'month/day,year': {
      const calculatedDate = new Date(value).toLocaleDateString('en-US', {
        day: 'numeric',
        year: 'numeric',
        month: 'long',
      });

      return calculatedDate;
    }
    case 'short/month/day,year': {
      const calculatedDate = new Date(value).toLocaleDateString('en-US', {
        day: 'numeric',
        year: 'numeric',
        month: 'short',
      });

      return calculatedDate;
    }
  }
};

export const addOrRemoveDaysToGivenDate = (date: Date, daysToAdd: number) => {
  return new Date(new Date(date).setDate(new Date().getDate() + daysToAdd));
};

export const getFirstDayOfMonth = (year: number, month: number) => {
  return new Date(year, month, 1);
};
export const getLastDayOfMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0);
};

export const addOrRemoveMonthsToGivenDate = (
  date: Date,
  monthsToAdd: number
) => {
  return new Date(new Date(date).setMonth(new Date().getMonth() + monthsToAdd));
};

export const addOrRemoveYearsToGivenDate = (date: Date, yearsToAdd: number) => {
  return new Date(
    new Date(date).setFullYear(new Date().getFullYear() + yearsToAdd)
  );
};

export const getEffectiveDate = (todayDate: number) => {
  if (todayDate <= 20) {
    const firstDayOfTheCurrentMonth = new Date(
      new Date().getFullYear(),
      new Date().getMonth() + 1,
      1
    );

    return formatDate(firstDayOfTheCurrentMonth);
  } else {
    const firstDayOfTheMontheAfterTwoMonthsFromNow = new Date(
      new Date().getFullYear(),
      addOrRemoveMonthsToGivenDate(new Date(), 2).getMonth(),
      1
    );

    return formatDate(firstDayOfTheMontheAfterTwoMonthsFromNow);
  }
};

export const formatPrice = (
  value: number,
  locales: string = 'en-US',
  { currency = 'USD', style = 'currency', minimumFractionDigits = 2 }: any = {}
) => {
  return value.toLocaleString(locales, {
    style: style,
    currency: currency,
    minimumFractionDigits: minimumFractionDigits,
  });
};

export const convertToUnits = (value: number, locales: string = 'en') => {
  const formatter = Intl.NumberFormat(locales, { notation: 'compact' });
  return formatter.format(value);
};

export const formatBytes = (bytes: number, decimals: number = 2) => {
  if (bytes === 0) return '0 Bytes';

  const base = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(base));

  return `${parseFloat((bytes / Math.pow(base, i)).toFixed(dm))} ${sizes[i]}`;
};

export const formatSSN = (value: string) => {
  if (!value) return value;
  const ssn = value.replace(new RegExp(/[^\d]/, 'ig'), '');
  const ssnLength = ssn.length;

  if (ssnLength < 4) return ssn;

  if (ssnLength < 6) {
    return `${ssn.slice(0, 3)}-${ssn.slice(3)}`;
  }

  return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5, 9)}`;
};

export const ssnNumberMask = (value: string) => {
  const ssn = value.replace(/\d(?=\d{4})/g, '*');

  return `${ssn.slice(0, 3)} - ${ssn.slice(3, 5)} - ${ssn.slice(5, 9)}`;
};

export const chunk = (arr: any, size: any) =>
  arr.reduce(
    (acc: any, _: any, i: any) =>
      i % size ? acc : [...acc, arr.slice(i, i + size)],
    []
  );

//Kids, don't try this at home!
export const openExternalLink = (url: string) => {
  let link = document.createElement('a');
  link.id = 'url';
  link.href = url;
  link.setAttribute('target', '_blank');

  link.click();
};

export const formatCardNumber = (value: any) => {
  const regex = /^(\d{0,4})(\d{0,4})(\d{0,4})(\d{0,4})$/g;
  const onlyNumbers = value.replace(/[^\d]/g, '');

  return onlyNumbers.replace(
    regex,
    (regex: any, $1: any, $2: any, $3: any, $4: any) =>
      [$1, $2, $3, $4].filter((group) => !!group).join(' ')
  );
};

export const formatExpirationDate = (value: any) => {
  return value
    .replace(
      /[^0-9]/g,
      '' // To allow only numbers
    )
    .replace(
      /^([2-9])$/g,
      '0$1' // To handle 3 > 03
    )
    .replace(
      /^(1{1})([3-9]{1})$/g,
      '0$1/$2' // 13 > 01/3
    )
    .replace(
      /^0{1,}/g,
      '0' // To handle 00 > 0
    )
    .replace(
      /^([0-1]{1}[0-9]{1})([0-9]{1,2}).*/g,
      '$1/$2' // To handle 113 > 11/3
    );
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const stopVideo = () => {
  const iframe = document.querySelector('iframe');

  if (iframe) {
    let iframeSrc = iframe.src;
    iframe.src = iframeSrc;
  }
};

export const generateIntegerRange = (
  start: number,
  stop: number,
  step: number = 1
) =>
  Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);

export const convertPercentageToDecimal = (value: number) =>
  roundTwoDecimals(value / 100);

export const roundTwoDecimals = (value: number): number =>
  Number(value.toFixed(2));

export const stepScrollToTop = () => window.scrollTo(0, 0);

export const COBRA_PERSONAS = [
  'persona-hw-cobra',
  'persona-hw-inactive',
  'persona-hw-db-inactive',
  'persona-hw-db-dc-cobra',
];
